import * as React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useProfile } from "./ProfileContext";

/**
 * Route wrapper that gates everything in the app on the user having
 * picked a handle. Companion to {@link ProfileSetupPage} (which is the
 * one route that bypasses this gate).
 *
 * <p>Three states from {@link useProfile}:</p>
 * <ul>
 *   <li>{@code loading} — show a comic-styled spinner stub so we don't
 *       flash the protected page before the gate decides.</li>
 *   <li>{@code error} — surface the message and a retry button. The
 *       fetch is run from the provider so retry just re-mounts via
 *       {@code refresh()}.</li>
 *   <li>{@code ready} — if handle is null, redirect to
 *       {@code /profile/setup}; otherwise admit the route.</li>
 * </ul>
 */
export function RequireHandle({ children }: { children: React.ReactNode }) {
  const { state, refresh } = useProfile();
  const location = useLocation();

  if (state.status === "loading") {
    return (
      <div className="mx-auto max-w-md p-10 text-center font-comic-body">
        <p className="font-comic-headline text-sm uppercase tracking-wider text-muted-foreground">
          Loading your profile…
        </p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="mx-auto max-w-md p-10 text-center font-comic-body">
        <p className="font-comic-headline text-sm uppercase tracking-wider text-destructive-strong">
          Couldn't load your profile
        </p>
        <p className="mt-2 text-sm text-muted-foreground">{state.error}</p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="comic-shadow-sm mt-4 rounded-md border-2 border-border bg-brand-blue text-brand-blue-foreground px-4 py-2 font-comic-headline uppercase tracking-wide hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
        >
          Try again
        </button>
      </div>
    );
  }

  // First-run gate: no handle → the one-time setup page, which now also *requires*
  // first + last name, so every new user provides both together. (We gate on handle,
  // not name: a legacy handle-having row can't re-run the immutable-handle setup, and
  // gating it on name would trap it — its name is captured via the profile form / the
  // V46 backfill instead.) Loose == catches undefined too.
  if (state.profile.handle == null) {
    // Preserve the page they were trying to reach so we can bounce them
    // back after setup.
    return (
      <Navigate
        to="/profile/setup"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  return <>{children}</>;
}
